import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCounters } from '../../contexts/CounterContext';
import { useToast } from '../../utils/toastUtils';
import ToastContainer from '../common/ToastContainer';
import ErrorAlert from '../common/ErrorAlert';
import CreateCounterModal from '../modals/CreateCounterModal';
import SimulatedTapCounter from '../common/SimulatedTapCounter';
import { Modal } from 'bootstrap';
import { useMetaTags } from '../../hooks/useMetaTags';

function Landing() {
  const { t } = useTranslation('landing');
  const navigate = useNavigate();
  const location = useLocation();
  const { addCounter } = useCounters();
  const { toasts, showToast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const modalRef = useRef<Modal | null>(null);

  // Check for login required parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const loginRequired = searchParams.get('login');

    if (loginRequired === 'required') {
      showToast(
        t('auth.loginRequired', 'Please log in to access that page'),
        'warning'
      );

      // Clean up the URL by removing the query parameter
      navigate('/', { replace: true });
    }
  }, [location, navigate, showToast, t]);

  // Use the custom hook for meta tags with specific homepage values
  useMetaTags(
    'TapDonkey - Create and Share Interactive Counters',
    'Create custom interactive counters for events, goals, and statistics. Share your counters with anyone for free and track results in real-time.',
  );

  // Add structured data for SEO
  useEffect(() => {
    // Create structured data for the product and its features
    const productStructuredData = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      'name': 'TapDonkey',
      'applicationCategory': 'WebApplication',
      'operatingSystem': 'All',
      'description': 'Tap-to-vote counters with optional payments—free for creators, powerful for engagement.',
      'offers': {
        '@type': 'Offer',
        'price': '0',
        'priceCurrency': 'USD'
      },
      'featureList': [
        'Real-time counter tracking',
        'Multi-language support',
        'Token system for monetization',
        'Secure Stripe Connect integration',
        'Customizable counter settings'
      ]
    };

    // Inject structured data into the document head
    const injectStructuredData = () => {
      // Remove any existing structured data scripts
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach(script => script.remove());

      // Add product structured data
      const productScript = document.createElement('script');
      productScript.type = 'application/ld+json';
      productScript.textContent = JSON.stringify(productStructuredData);
      document.head.appendChild(productScript);
    };

    injectStructuredData();

    // Clean up on component unmount
    return () => {
      const structuredDataScripts = document.querySelectorAll('script[type="application/ld+json"]');
      structuredDataScripts.forEach(script => script.remove());
    };
  }, [t]); // Re-run when language changes to update translated content

  const openCreateModal = () => {
    const modalEl = document.getElementById('createCounterModal');
    if (modalEl) {
      const modal = new Modal(modalEl);
      modalRef.current = modal;
      modal.show();
    }
  };

  const handleCreateCounter = async (name: string, isPrivate?: boolean, email?: string) => {
    try {
      setIsCreating(true);
      const newCounter = await addCounter(name, isPrivate, email);

      // Hide the modal
      if (modalRef.current) {
        modalRef.current.hide();
      }

      // Navigate to the new counter
      navigate(`/${newCounter.ownerUrl}`);
    } catch (err) {
      console.error('Failed to create counter:', err);
      setCreateError(t('errors.failedToCreate', 'Failed to create counter'));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Hero Section - Modern, Clean Design with Logo */}
      <div className="bg-gradient bg-primary py-4 py-md-5 position-relative overflow-hidden">
        <div className="container py-3 py-md-5 position-relative">
          <div className="row align-items-center py-2 py-md-4">
            <div className="col-lg-6 text-center text-lg-start text-white">
              <div className="d-flex align-items-center mb-3 mb-md-4 justify-content-center justify-content-lg-start">
                <img
                  src="/images/favicon/favicon.svg"
                  alt="TapDonkey Logo"
                  className="me-3 d-none d-sm-block"
                  width="60"
                  height="60"
                />
                <img
                  src="/images/favicon/favicon.svg"
                  alt="TapDonkey Logo"
                  className="me-2 d-sm-none"
                  width="40"
                  height="40"
                />
                <div>
                  <h1 className="display-5 display-md-4 fw-bold mb-0">{t('title')}</h1>
                  <div className="border-top border-white border-opacity-25 w-100 mt-2"></div>
                </div>
              </div>
              <p className="lead mb-3 mb-md-4">{t('description')}</p>
              <div className="card border-0 rounded-4 mb-3 mb-md-4 shadow-sm d-none d-sm-block">
                <div className="card-body p-3">
                  <div className="d-flex">
                    <i className="bi bi-lightbulb-fill fs-4 text-primary me-3"></i>
                    <div>
                      <h5 className="mb-1 fs-6 fw-bold">{t('getStartedTip')}</h5>
                      <p className="mb-0 small">{t('getStartedDesc')}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-grid d-sm-flex gap-2 gap-sm-3">
                <button
                  className="btn btn-light btn-lg px-3 px-md-4 py-2 fw-medium shadow-sm text-primary"
                  onClick={openCreateModal}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  <span className="d-none d-sm-inline">{t('counters.createNew')}</span>
                  <span className="d-sm-none">{t('counters.createNew')}</span>
                </button>
                <Link to="/counters" className="btn btn-outline-light btn-lg px-3 px-md-4 py-2">
                  <i className="bi bi-collection me-2"></i>
                  <span className="d-none d-sm-inline">{t('counters.title')}</span>
                  <span className="d-sm-none">{t('counters.viewAll')}</span>
                </Link>
              </div>
            </div>
            <div className="col-lg-6 d-none d-lg-block text-center mt-5 mt-lg-0">
              <div className="p-1 bg-white bg-opacity-10 rounded-4 shadow-lg">
                <div className="bg-white rounded-3 shadow p-4">
                  <SimulatedTapCounter />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Value Proposition Section - How It Works */}
      <section className="bg-body py-4 py-lg-5">
        <div className="container py-3 py-lg-4">
          <div className="row text-center mb-4 mb-lg-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="fw-bold mb-2 mb-lg-3">{t('features.title')}</h2>
              <p className="lead text-body-secondary">{t('features.subtitle')}</p>
            </div>
          </div>

          {/* Modern Stepper/Timeline */}
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch gap-4">
                {/* Step 1 */}
                <div className="card border-0 rounded-4 shadow-sm flex-fill text-center howitworks-step h-100">
                  <div className="card-body p-4 d-flex flex-column align-items-center">
                    <i className="bi bi-plus-circle-fill display-4 text-primary mb-3"></i>
                    <h3 className="h5 fw-bold mb-2">1. {t('features.steps.step1Title')}</h3>
                    <p className="text-body-secondary mb-0 small">{t('features.steps.step1Desc')}</p>
                  </div>
                </div>
                {/* Step 2 */}
                <div className="card border-0 rounded-4 shadow-sm flex-fill text-center howitworks-step h-100">
                  <div className="card-body p-4 d-flex flex-column align-items-center">
                    <i className="bi bi-ui-checks-grid display-4 text-primary mb-3"></i>
                    <h3 className="h5 fw-bold mb-2">2. {t('features.steps.step2Title')}</h3>
                    <p className="text-body-secondary mb-0 small">{t('features.steps.step2Desc')}</p>
                  </div>
                </div>
                {/* Step 3 */}
                <div className="card border-0 rounded-4 shadow-sm flex-fill text-center howitworks-step h-100">
                  <div className="card-body p-4 d-flex flex-column align-items-center">
                    <i className="bi bi-share-fill display-4 text-primary mb-3"></i>
                    <h3 className="h5 fw-bold mb-2">3. {t('features.steps.step3Title')}</h3>
                    <p className="text-body-secondary mb-0 small">{t('features.steps.step3Desc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Token System Section */}
      <div className="py-4 py-lg-5 bg-body-tertiary">
        <div className="container py-3 py-lg-4">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 className="fw-bold mb-3 text-center text-lg-start">{t('tokenSystem.title')}</h2>
              <p className="lead mb-4 text-center text-lg-start">{t('tokenSystem.subtitle')}</p>
              
              <div className="d-flex align-items-center mb-3">
                <div className="bg-primary-subtle text-primary-emphasis p-2 me-3 rounded-3">
                  <i className="bi bi-currency-dollar"></i>
                </div>
                <p className="mb-0">{t('tokenSystem.tokenCost')}</p>
              </div>
              <div className="d-flex align-items-center mb-3">
                <div className="bg-primary-subtle text-primary-emphasis p-2 me-3 rounded-3">
                  <i className="bi bi-coin"></i>
                </div>
                <p className="mb-0">{t('tokenSystem.earnTokens')}</p>
              </div>
              <div className="d-flex align-items-center mb-3">
                <div className="bg-primary-subtle text-primary-emphasis p-2 me-3 rounded-3">
                  <i className="bi bi-wallet2"></i>
                </div>
                <p className="mb-0">{t('tokenSystem.withdraw')}</p>
              </div>
              <div className="d-flex align-items-center mb-3">
                <div className="bg-primary-subtle text-primary-emphasis p-2 me-3 rounded-3">
                  <i className="bi bi-graph-up"></i>
                </div>
                <p className="mb-0">{t('tokenSystem.createExperiences')}</p>
              </div>
              
              <p className="small text-body-secondary mt-3">{t('tokenSystem.withdrawalNote')}</p>
            </div>
            <div className="col-lg-6">
              <div className="card border-0 rounded-4 shadow-sm bg-body p-3 p-lg-4 mx-auto" style={{ maxWidth: "500px" }}>
                <div className="d-flex align-items-center mb-4">
                  <div className="bg-primary text-white rounded-3 p-3 me-3">
                    <i className="bi bi-coin fs-3"></i>
                  </div>
                  <div>
                    <h4 className="fw-bold mb-1">{t('tokenSystem.cardTitle')}</h4>
                    <p className="mb-0 text-body-secondary">{t('tokenSystem.cardSubtitle')}</p>
                  </div>
                </div>
                <div className="mb-3 p-3 bg-body-tertiary rounded-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <i className="bi bi-1-circle-fill text-primary fs-3"></i>
                    </div>
                    <div>
                      <h5 className="mb-1">{t('tokenSystem.createButtonsTitle')}</h5>
                      <p className="mb-0 small">{t('tokenSystem.createButtonsDesc')}</p>
                    </div>
                  </div>
                </div>
                <div className="mb-3 p-3 bg-body-tertiary rounded-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <i className="bi bi-2-circle-fill text-primary fs-3"></i>
                    </div>
                    <div>
                      <h5 className="mb-1">{t('tokenSystem.earnTitle')}</h5>
                      <p className="mb-0 small">{t('tokenSystem.earnDesc')}</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-body-tertiary rounded-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <i className="bi bi-3-circle-fill text-primary fs-3"></i>
                    </div>
                    <div>
                      <h5 className="mb-1">{t('tokenSystem.withdrawTitle')}</h5>
                      <p className="mb-0 small">{t('tokenSystem.withdrawDesc')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Use Cases Section */}
      <div className="py-4 py-lg-5 bg-body">
        <div className="container py-3 py-lg-4">
          <div className="row text-center mb-4 mb-lg-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="fw-bold mb-2 mb-lg-3">{t('useCases.title')}</h2>
              <p className="lead text-body-secondary">{t('useCases.subtitle')}</p>
            </div>
          </div>
          
          <div className="row g-3 g-lg-4 justify-content-center">
            <div className="col-md-4">
              <div className="card h-100 border-0 rounded-4 shadow-sm">
                <div className="card-body p-3 p-lg-4">
                  <div className="text-primary mb-3">
                    <i className="bi bi-stopwatch-fill fs-1"></i>
                  </div>
                  <h3 className="h5 fw-bold">{t('useCases.clickCompetitions')}</h3>
                  <p className="card-text text-body-secondary">{t('useCases.clickCompetitionsDesc')}</p>
                </div>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="card h-100 border-0 rounded-4 shadow-sm">
                <div className="card-body p-3 p-lg-4">
                  <div className="text-primary mb-3">
                    <i className="bi bi-people-fill fs-1"></i>
                  </div>
                  <h3 className="h5 fw-bold">{t('useCases.teamDecision')}</h3>
                  <p className="card-text text-body-secondary">{t('useCases.teamDecisionDesc')}</p>
                </div>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="card h-100 border-0 rounded-4 shadow-sm">
                <div className="card-body p-3 p-lg-4">
                  <div className="text-primary mb-3">
                    <i className="bi bi-emoji-laughing fs-1"></i>
                  </div>
                  <h3 className="h5 fw-bold">{t('useCases.liveReactions')}</h3>
                  <p className="card-text text-body-secondary">{t('useCases.liveReactionsDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary text-white py-4 py-lg-5 position-relative overflow-hidden">
        <div className="container py-3 py-lg-4 position-relative">
          <div className="row justify-content-center text-center">
            <div className="col-lg-10 col-xl-8">
              <h2 className="fw-bold mb-3 mb-lg-4">{t('cta.title')}</h2>
              <p className="mb-3 mb-lg-4 lead">{t('cta.description')}</p>
              
                            <div className="card border-0 rounded-4 shadow-sm mb-4 mx-auto bg-light" style={{ maxWidth: "450px" }}>                <div className="card-body p-3 p-lg-4">                  <h5 className="card-title fw-bold mb-3 text-dark">{t('howToStart')}</h5>                  <ol className="text-start mb-0 ps-4 text-dark">                    <li className="mb-2">{t('step1')}</li>                    <li className="mb-2">{t('step2')}</li>                    <li className="mb-2">{t('step3')}</li>                    <li>{t('step4')}</li>                  </ol>                </div>              </div>
              
              <button
                className="btn btn-light btn-lg px-4 px-md-5 py-2 py-md-3 fw-medium shadow-sm"
                onClick={openCreateModal}
              >
                <i className="bi bi-plus-circle me-2"></i>
                {t('counters.createNew')}
              </button>
              <p className="mt-3 small text-white-50">{t('quickStart')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Create Counter Modal */}
      <CreateCounterModal
        isCreating={isCreating}
        onCreateCounter={handleCreateCounter}
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} title={t('counters.title')} />
    </div>
  );
}

export default Landing; 